import React,{useEffect,useMemo,useState}from'react';
import*as FiIcons from'react-icons/fi';
import SafeIcon from'../common/SafeIcon';
import{timelineDefaults}from'../data/timelineDefaults';
import{getTimelineConflicts}from'../utils/timelineConflicts';
import'./timeline.css';

const{
  FiAlertTriangle,
  FiCheckCircle,
  FiChevronDown,
  FiChevronUp,
  FiGripVertical,
  FiPlus,
  FiTrash2
}=FiIcons;

function createCustomEvent(){
  return{
    id:`custom-${Date.now()}`,
    time:'',
    name:'New Event',
    description:'',
    music:'',
    notes:''
  };
}

function formatConflictLabel(conflict){
  return conflict.type==='duplicate'
    ?'Resolve duplicate time'
    :'Fix timeline order';
}

function ConflictResolutionPanel({conflicts,onResolve}){
  if(conflicts.length===0){
    return null;
  }

  return(
    <div className="timeline-conflict-alert" role="alert">
      <SafeIcon icon={FiAlertTriangle}/>

      <div className="timeline-conflict-content">
        <strong>
          {conflicts.length===1
            ?'Timeline conflict found'
            :`${conflicts.length} timeline conflicts found`}
        </strong>

        <div className="timeline-conflict-list">
          {conflicts.map((conflict)=>(
            <div className="timeline-conflict-item" key={conflict.id}>
              <p>{conflict.message}</p>
              <button
                type="button"
                className="timeline-conflict-action"
                onClick={()=>onResolve(conflict)}
              >
                <SafeIcon icon={FiCheckCircle}/>
                {formatConflictLabel(conflict)}
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function TimelineBuilder({items,setItems}){
  const[draggedId,setDraggedId]=useState(null);

  useEffect(()=>{
    const isLegacyTimeline=
      items.length===5&&items[0]?.name==='Ceremony';

    if(isLegacyTimeline){
      setItems(timelineDefaults.map((item)=>({...item})));
    }
  },[items,setItems]);

  const conflicts=useMemo(
    ()=>getTimelineConflicts(items),
    [items]
  );

  const conflictIndexes=useMemo(
    ()=>new Set(
      conflicts.flatMap((conflict)=>conflict.indexes)
    ),
    [conflicts]
  );

  const update=(id,key,value)=>{
    setItems((current)=>current.map((item)=>
      item.id===id
        ?{...item,[key]:value}
        :item
    ));
  };

  const remove=(id)=>{
    setItems((current)=>
      current.filter((item)=>item.id!==id)
    );
  };

  const move=(index,direction)=>{
    const next=[...items];
    const target=index+direction;

    if(target<0||target>=next.length){
      return;
    }

    [next[index],next[target]]=[
      next[target],
      next[index]
    ];

    setItems(next);
  };

  const handleDrop=(targetId)=>{
    if(!draggedId||draggedId===targetId){
      setDraggedId(null);
      return;
    }

    const next=[...items];
    const sourceIndex=next.findIndex(
      (item)=>item.id===draggedId
    );
    const targetIndex=next.findIndex(
      (item)=>item.id===targetId
    );

    if(sourceIndex<0||targetIndex<0){
      setDraggedId(null);
      return;
    }

    const[moved]=next.splice(sourceIndex,1);
    next.splice(targetIndex,0,moved);
    setItems(next);
    setDraggedId(null);
  };

  const resolveConflict=(conflict)=>{
    if(conflict.type==='duplicate'){
      const duplicateIndexes=conflict.indexes.slice(1);

      setItems((current)=>current.map((item,index)=>
        duplicateIndexes.includes(index)
          ?{...item,time:''}
          :item
      ));

      return;
    }

    const timedIndexes=items
      .map((item,index)=>({
        item,
        index,
        minutes:toMinutes(item.time)
      }))
      .filter((entry)=>entry.minutes!==null);

    const sortedItems=timedIndexes
      .map((entry)=>entry.item)
      .sort((first,second)=>
        toMinutes(first.time)-toMinutes(second.time)
      );

    setItems((current)=>{
      const next=[...current];

      timedIndexes.forEach((entry,index)=>{
        next[entry.index]=sortedItems[index];
      });

      return next;
    });
  };

  const add=()=>{
    setItems((current)=>[
      ...current,
      createCustomEvent()
    ]);
  };

  return(
    <div className="timeline-editor">
      <div className="timeline-intro">
        <div>
          <span className="eyebrow">Build your run of show</span>
          <h3>Event timeline</h3>
          <p>
            Add each moment in the order you want it to happen.
            You can change the order at any time.
          </p>
        </div>

        <span className="timeline-count">
          {items.length} moments
        </span>
      </div>

      <ConflictResolutionPanel
        conflicts={conflicts}
        onResolve={resolveConflict}
      />

      <div className="timeline-table-wrap">
        <table className="timeline-table">
          <thead>
            <tr>
              <th className="timeline-order-heading">Order</th>
              <th className="timeline-time-heading">Time</th>
              <th>Event</th>
              <th>Description</th>
              <th className="timeline-actions-heading">Move</th>
              <th className="timeline-actions-heading">Remove</th>
            </tr>
          </thead>

          <tbody>
            {items.map((item,index)=>(
              <tr
                key={item.id}
                draggable
                className={`timeline-table-row ${
                  conflictIndexes.has(index)
                    ?'timeline-conflict-row'
                    :''
                }`}
                onDragStart={()=>setDraggedId(item.id)}
                onDragOver={(event)=>event.preventDefault()}
                onDrop={()=>handleDrop(item.id)}
                onDragEnd={()=>setDraggedId(null)}
              >
                <td className="timeline-order-cell">
                  <span
                    className="timeline-drag-handle"
                    title="Drag to reorder"
                  >
                    <SafeIcon icon={FiGripVertical}/>
                  </span>
                  <strong>
                    {String(index+1).padStart(2,'0')}
                  </strong>
                </td>

                <td className="timeline-time-cell">
                  <input
                    type="time"
                    value={item.time||''}
                    onChange={(event)=>
                      update(item.id,'time',event.target.value)
                    }
                    aria-label={`${item.name} time`}
                  />
                </td>

                <td className="timeline-event-cell">
                  <input
                    value={item.name||''}
                    onChange={(event)=>
                      update(item.id,'name',event.target.value)
                    }
                    placeholder="Event name"
                    aria-label={`${item.name} event name`}
                  />
                </td>

                <td className="timeline-description-cell">
                  <textarea
                    value={item.description||item.notes||''}
                    onChange={(event)=>{
                      update(
                        item.id,
                        'description',
                        event.target.value
                      );
                      update(
                        item.id,
                        'notes',
                        event.target.value
                      );
                    }}
                    placeholder="What happens here? Add people, announcements, music or notes..."
                    rows="2"
                    aria-label={`${item.name} description`}
                  />
                </td>

                <td className="timeline-reorder-cell">
                  <div className="timeline-reorder-buttons">
                    <button
                      type="button"
                      className="timeline-icon-button"
                      disabled={index===0}
                      onClick={()=>move(index,-1)}
                      aria-label={`Move ${item.name} up`}
                    >
                      <SafeIcon icon={FiChevronUp}/>
                    </button>

                    <button
                      type="button"
                      className="timeline-icon-button"
                      disabled={index===items.length-1}
                      onClick={()=>move(index,1)}
                      aria-label={`Move ${item.name} down`}
                    >
                      <SafeIcon icon={FiChevronDown}/>
                    </button>
                  </div>
                </td>

                <td className="timeline-remove-cell">
                  <button
                    type="button"
                    className="timeline-icon-button timeline-delete-button"
                    onClick={()=>remove(item.id)}
                    aria-label={`Remove ${item.name}`}
                  >
                    <SafeIcon icon={FiTrash2}/>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <button
        type="button"
        className="add-question timeline-add-button"
        onClick={add}
      >
        <SafeIcon icon={FiPlus}/>
        Add custom timeline event
      </button>
    </div>
  );
}

function toMinutes(value){
  if(!value){
    return null;
  }

  const[hours,minutes]=value.split(':').map(Number);

  if(
    Number.isNaN(hours)||
    Number.isNaN(minutes)||
    hours<0||
    hours>23||
    minutes<0||
    minutes>59
  ){
    return null;
  }

  return hours*60+minutes;
}

export default TimelineBuilder;